import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

const statusText = {
  paper_on_file_scan_pending: "paper signed · scan pending",
  scan_uploaded: "scan uploaded",
  verified: "verified",
};
const matchText = {
  pending_review: "pending review",
  matches: "digital matches paper",
  needs_correction: "needs correction",
  missing_digital_content: "missing digital content",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function RecordRow({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [orient, setOrient] = useState(null);
  const [signKey, setSignKey] = useState("");
  const [signDate, setSignDate] = useState("");
  const [orientedBy, setOrientedBy] = useState("");

  const loadOrientation = async () => {
    if (orient) { setOrient(null); return; }
    setBusy("orient");
    try {
      const r = await api.get(`/admin/clients/${row.client_id}/orientation`);
      setOrient(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const printOrientation = async (cg) => {
    setBusy(`orient-print-${cg}`);
    try {
      await api.post(`/admin/orientation-acks`, { client_id: row.client_id, caregiver_name: cg });
      const res = await api.get(`/admin/orientation-acks/print.pdf?client_id=${row.client_id}&caregiver_name=${encodeURIComponent(cg)}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      loadOrientation();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const signOrientation = async (ackId, cg) => {
    setBusy(`orient-sign-${cg}`);
    try {
      await api.put(`/admin/orientation-acks/${ackId}/sign`, { signed_date: signDate || undefined, oriented_by: orientedBy || undefined });
      toast.success(`Orientation recorded for ${cg}`);
      setSignKey(""); setSignDate(""); setOrientedBy("");
      loadOrientation();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const [review, setReview] = useState({
    signature_date: row.signature_date || "", verified_by: row.verified_by || "",
    match_status: row.match_status || "pending_review", review_notes: row.review_notes || "",
    wet_signature_status: row.wet_signature_status || "paper_on_file_scan_pending",
  });
  const set = (key) => (e) => setReview({ ...review, [key]: e.target.value });
  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/careplan-records/${row.id}/signed-file`, fd);
      toast.success("Signed care plan scan uploaded");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const saveReview = async () => {
    setBusy("review");
    try {
      await api.put(`/careplan-records/${row.id}/review`, review);
      toast.success("Care plan review saved");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const refresh = async () => {
    setBusy("refresh");
    try {
      await api.post(`/careplan-records/${row.id}/refresh`);
      toast.success("Digital snapshot refreshed");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const download = async () => {
    setBusy("download");
    try {
      const res = await api.get(`/careplan-records/${row.id}/signed-file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };
  const downloadPoc = async (docId) => {
    setBusy(`poc-${docId}`);
    try {
      const res = await api.get(`/poc-documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };


  const downloadArchive = async (docId) => {
    setBusy(`arc-${docId}`);
    try {
      const res = await api.get(`/archive-documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const printPlan = async (variant) => {
    setBusy(`print-${variant}`);
    try {
      const res = await api.get(`/clients/${row.client_id}/careplan/print.pdf?variant=${variant}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border-b border-stone-100" data-testid={`careplan-record-${row.id}`}>
      <button onClick={() => setOpen(!open)} className="grid w-full grid-cols-1 gap-2 px-4 py-3 text-left hover:bg-stone-50 lg:grid-cols-[1.2fr_0.7fr_0.7fr_1fr_0.9fr_0.8fr] lg:items-center" data-testid={`careplan-record-toggle-${row.id}`}>
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-stone-800">{row.client_name}</span>
          <span className="text-[10px] text-stone-400">{row.client_status}</span>
        </span>
        <span className="text-xs text-stone-600">Effective · {fmtDate(row.effective_date || (row.has_minimum_viable_plan && !row.minimum_viable_superseded_by ? row.minimum_viable_plan?.effective_date : ""))}</span>
        <span className="text-xs text-stone-600" data-testid={`careplan-digital-status-${row.id}`}>{row.has_digital_plan ? `${row.task_count} tasks` : (row.has_minimum_viable_plan && !row.minimum_viable_superseded_by ? <span className="font-medium text-[#8a5d14]">Draft · {(row.minimum_viable_plan?.tasks || []).filter((t) => t.selected).length} tasks · pending review</span> : "digital content missing")}</span>
        <span className="text-xs text-stone-600" data-testid={`careplan-signature-status-${row.id}`}>{statusText[row.wet_signature_status] || row.wet_signature_status}</span>
        <span className={`text-xs font-semibold ${row.match_status === "needs_correction" ? "text-[#C0392B]" : row.match_status === "matches" ? "text-emerald-700" : "text-[#8a5d14]"}`}>{matchText[row.match_status]}</span>
        <span className="text-xs text-stone-500">{row.verified_by || "not verified"}</span>
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-4 bg-stone-50 px-4 py-4 lg:grid-cols-[1fr_1.2fr]" data-testid={`careplan-record-panel-${row.id}`}>
          <div className="space-y-3 text-xs text-stone-600">
            <p><strong>Paper source:</strong> wet-signed care plan is the source of truth. Paper remains the backup; this record is the working digital archive.</p>
            <p><strong>Schedule:</strong> {row.schedule_instructions || "—"}</p>
            <p><strong>Weekly hours:</strong> {row.total_weekly_hours || "—"}</p>
            {row.has_minimum_viable_plan && !row.minimum_viable_superseded_by && row.minimum_viable_plan && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`careplan-mvp-draft-${row.id}`}>
                <p className="text-xs font-semibold text-[#8a5d14]">Minimum viable draft — pending owner review</p>
                <p className="mt-1 text-[11px] text-stone-600">Source: {row.minimum_viable_source_label || "—"}</p>
                <p className="mt-1 text-[11px] text-stone-600">Tasks: {(row.minimum_viable_plan.tasks || []).filter((t) => t.selected).map((t) => `${t.code} ${t.name}`).join(" · ") || "none"}</p>
                {row.latest_poc_snapshot && (
                  <p className="mt-1 text-[11px] text-stone-500" data-testid={`careplan-latest-poc-${row.id}`}>
                    Most recent POC ({fmtDate(row.latest_poc_snapshot.poc_start_date)}): {row.latest_poc_snapshot.task_codes.length} tasks
                    {(row.latest_poc_snapshot.added_vs_initial || []).length > 0 && ` · adds ${row.latest_poc_snapshot.added_vs_initial.join(", ")}`}
                    {(row.latest_poc_snapshot.removed_vs_initial || []).length > 0 && ` · drops ${row.latest_poc_snapshot.removed_vs_initial.join(", ")}`}
                  </p>
                )}
              </div>
            )}
            {row.poc_comparison && (
              <div className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`careplan-poc-comparison-${row.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold text-stone-700">
                    Latest POC vs current plan
                    {row.poc_comparison.has_plan && row.poc_comparison.has_poc && <span className="ml-2 text-xs font-bold text-primary" data-testid={`poc-coverage-${row.id}`}>{row.poc_comparison.coverage_percent}% match</span>}
                  </p>
                  <Link to={`/careplan-records/${row.id}/compare`} className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-700 hover:bg-white" data-testid={`open-comparison-${row.id}`}>Open full comparison</Link>
                </div>
              </div>
            )}
            {(row.source_documents || []).length > 0 && (
              <div className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`careplan-poc-sources-${row.id}`}>
                <p className="text-xs font-semibold text-stone-700">POC source documents ({row.source_documents.length})</p>
                <div className="mt-2 space-y-2">
                  {row.source_documents.map((doc) => (
                    <div key={doc.id} className="rounded-md border border-stone-100 p-2">
                      <button onClick={() => downloadPoc(doc.id)} className="text-xs font-semibold text-primary hover:underline" data-testid={`poc-download-${doc.id}`}>
                        {busy === `poc-${doc.id}` ? "Opening…" : doc.filename}
                      </button>
                      <p className="mt-1 text-[11px] text-stone-500">POC {fmtDate(doc.poc_start_date)}{doc.poc_stop_date ? ` – ${fmtDate(doc.poc_stop_date)}` : ""} · report {fmtDate(doc.report_date)}</p>
                      {doc.address && <p className="text-[11px] text-stone-500">{doc.address}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(row.archive_documents || []).length > 0 && (
              <div className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`careplan-archive-docs-${row.id}`}>
                <p className="text-xs font-semibold text-stone-700">Client document archive ({row.archive_documents.length})</p>
                <div className="mt-2 space-y-2">
                  {row.archive_documents.map((doc) => (
                    <div key={doc.id} className="rounded-md border border-stone-100 p-2">
                      <button onClick={() => downloadArchive(doc.id)} className="text-xs font-semibold text-primary hover:underline" data-testid={`archive-download-${doc.id}`}>
                        {busy === `arc-${doc.id}` ? "Opening…" : doc.filename}
                      </button>
                      <p className="mt-1 text-[11px] text-stone-500">
                        {doc.doc_type}{doc.has_signature_evidence ? ` · signed${doc.signed_at ? ` ${doc.signed_at}` : ""}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {(row.has_digital_plan && row.task_count > 0) && (
                <button onClick={() => printPlan("update")} disabled={busy === "print-update"} className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-stone-900 disabled:opacity-40" data-testid={`careplan-print-update-${row.id}`}>{busy === "print-update" ? "Preparing…" : "Print 2026 update"}</button>
              )}
              {((row.has_digital_plan && row.task_count > 0) || (row.has_minimum_viable_plan && !row.minimum_viable_superseded_by)) && (
                <button onClick={() => printPlan("original")} disabled={busy === "print-original"} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white disabled:opacity-40" data-testid={`careplan-print-original-${row.id}`}>{busy === "print-original" ? "Preparing…" : "Print original"}</button>
              )}
              <Link to={`/clients/${row.client_id}`} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white" data-testid={`careplan-open-client-${row.id}`}>Open client</Link>
              <button onClick={loadOrientation} disabled={busy === "orient"} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white disabled:opacity-40" data-testid={`careplan-orientation-${row.id}`}>{busy === "orient" ? "Loading…" : orient ? "Hide orientation forms" : "Orientation forms"}</button>
              <button onClick={refresh} disabled={busy === "refresh"} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white disabled:opacity-40" data-testid={`careplan-refresh-${row.id}`}>{busy === "refresh" ? "Refreshing…" : "Refresh digital snapshot"}</button>
              {row.signed_file && <button onClick={download} disabled={busy === "download"} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-semibold text-stone-700 hover:bg-white disabled:opacity-40" data-testid={`careplan-download-${row.id}`}>{busy === "download" ? "Opening…" : "Open signed scan"}</button>}
            </div>
            {orient && (
              <div className="rounded-lg border border-stone-200 bg-white p-3" data-testid={`orientation-panel-${row.id}`}>
                <p className="text-xs font-semibold text-stone-700">Care plan orientation acknowledgments — {orient.client_name}</p>
                <p className="mt-0.5 text-[11px] text-stone-500">Prepared blank for everyone; becomes part of the employee file only once filled out and signed. "Probable orienter" = had office hours on the client's first service day.</p>
                <div className="mt-2 space-y-2">
                  {(orient.rows || []).map((r2) => (
                    <div key={r2.caregiver_name} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-stone-100 p-2" data-testid={`orientation-row-${r2.caregiver_name.replace(/\s+/g, "-")}`}>
                      <div>
                        <p className="text-xs font-semibold text-stone-800">{r2.caregiver_name}
                          {r2.probable_orienter && <span className="ml-2 rounded-full bg-[#C49A3A]/15 px-2 py-0.5 text-[10px] font-semibold text-[#8a6a14]">probable orienter</span>}
                        </p>
                        <p className="text-[11px] text-stone-500">First worked: {fmtDate(r2.first_worked_date)}
                          {r2.ack && <> · {r2.ack.status === "signed" ? <span className="font-semibold text-[#1e8449]">signed {fmtDate(r2.ack.signed_at)}</span> : "prepared — awaiting signature"}</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => printOrientation(r2.caregiver_name)} disabled={busy === `orient-print-${r2.caregiver_name}`}
                          className="rounded-full border border-stone-300 px-3 py-1 text-[11px] font-semibold text-stone-700 hover:bg-white disabled:opacity-40"
                          data-testid={`orientation-print-${r2.caregiver_name.replace(/\s+/g, "-")}`}>
                          {busy === `orient-print-${r2.caregiver_name}` ? "Preparing…" : "Print form"}
                        </button>
                        {r2.ack && r2.ack.status !== "signed" && (
                          signKey === r2.ack.id ? (
                            <span className="flex items-center gap-1">
                              <input type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} className="h-7 rounded border border-stone-300 px-1.5 text-[11px]" data-testid={`orientation-sign-date-${r2.ack.id}`} />
                              <input value={orientedBy} onChange={(e) => setOrientedBy(e.target.value)} placeholder="Oriented by" className="h-7 w-28 rounded border border-stone-300 px-1.5 text-[11px]" data-testid={`orientation-oriented-by-${r2.ack.id}`} />
                              <button onClick={() => signOrientation(r2.ack.id, r2.caregiver_name)} className="rounded-full bg-[#2F6B4F] px-2.5 py-1 text-[11px] font-semibold text-white" data-testid={`orientation-sign-confirm-${r2.ack.id}`}>Save</button>
                              <button onClick={() => setSignKey("")} className="text-[11px] text-stone-400">Cancel</button>
                            </span>
                          ) : (
                            <button onClick={() => { setSignKey(r2.ack.id); setSignDate(""); setOrientedBy(r2.ack.oriented_by || ""); }}
                              className="rounded-full border border-[#2F6B4F]/40 px-3 py-1 text-[11px] font-semibold text-[#2F6B4F] hover:bg-[#2F6B4F]/10"
                              data-testid={`orientation-sign-${r2.ack.id}`}>Mark signed</button>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                  {(orient.rows || []).length === 0 && <p className="text-[11px] text-stone-500">No visit history yet — forms can be prepared once shifts are recorded.</p>}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div><Label className="text-xs">Signature date</Label><Input type="date" value={review.signature_date} onChange={set("signature_date")} className="mt-1 h-8 bg-white" data-testid={`careplan-signature-date-${row.id}`} /></div>
              <div><Label className="text-xs">Verified by</Label><Input value={review.verified_by} onChange={set("verified_by")} className="mt-1 h-8 bg-white" data-testid={`careplan-verified-by-${row.id}`} /></div>
              <div><Label className="text-xs">Digital vs paper</Label><select value={review.match_status} onChange={set("match_status")} className="mt-1 h-8 w-full rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`careplan-match-status-${row.id}`}>{Object.entries(matchText).map(([v, label]) => <option key={v} value={v}>{label}</option>)}</select></div>
              <div><Label className="text-xs">Wet signature status</Label><select value={review.wet_signature_status} onChange={set("wet_signature_status")} className="mt-1 h-8 w-full rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid={`careplan-wet-status-${row.id}`}>{Object.entries(statusText).map(([v, label]) => <option key={v} value={v}>{label}</option>)}</select></div>
            </div>
            <div><Label className="text-xs">Review notes / differences to correct</Label><textarea value={review.review_notes} onChange={set("review_notes")} rows={2} className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-xs" data-testid={`careplan-review-notes-${row.id}`} /></div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="cursor-pointer rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-stone-900" data-testid={`careplan-upload-label-${row.id}`}>
                {busy === "upload" ? "Uploading…" : row.signed_file ? "Replace signed scan" : "Upload signed scan"}
                <input type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`careplan-upload-${row.id}`} />
              </label>
              <Button size="sm" onClick={saveReview} disabled={busy === "review"} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`careplan-save-review-${row.id}`}>{busy === "review" ? "Saving…" : "Save review"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CarePlanRecordsPage() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/careplan-records").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (!loading && user?.role === "admin") load(); }, [loading, user]);
  const seed = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/careplan-records/seed-campaign");
      toast.success(`Care plan records ready: ${res.data.total} records`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadZip = async (variant) => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/careplan-records/print.zip?variant=${variant}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `care-plan-print-${variant}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const apiRows = data?.rows;
  const rows = useMemo(() => (apiRows || []).filter((r) => {
    const matches = !q.trim() || (r.client_name || "").toLowerCase().includes(q.trim().toLowerCase());
    if (!matches) return false;
    if (filter === "scan") return !r.signed_file;
    if (filter === "review") return r.match_status === "pending_review";
    if (filter === "correction") return r.match_status === "needs_correction";
    if (filter === "missing") return !r.has_digital_plan;
    return true;
  }), [apiRows, q, filter]);

  if (loading) return <div className="py-16 text-center text-sm text-stone-400">Loading…</div>;
  if (user?.role !== "admin") return <div className="rounded-xl border border-stone-200 bg-white p-8 text-sm text-stone-600" data-testid="careplan-records-no-access">Care plan records are office-only.</div>;
  if (!data) return <div className="py-16 text-center text-sm text-stone-400" data-testid="careplan-records-loading">Loading care plan records…</div>;

  return (
    <div className="space-y-6" data-testid="careplan-records-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Care Plan Records</h1>
          <p className="mt-1 max-w-3xl text-sm text-stone-500">Wet-signed paper is the source of truth. This office-only archive tracks the digital record, signed scan, reviewer, and whether the digital plan matches the paper.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => downloadZip("update")} disabled={busy} className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="careplan-print-zip-update">{busy ? "Working…" : "Print all — 2026 updates (ZIP)"}</Button>
          <Button onClick={() => downloadZip("original")} disabled={busy} variant="outline" className="rounded-full" data-testid="careplan-print-zip-original">{busy ? "Working…" : "Print all — originals (ZIP)"}</Button>
          <Button onClick={seed} disabled={busy} variant="outline" className="rounded-full" data-testid="careplan-records-seed">{busy ? "Refreshing…" : "Refresh 54-client record list"}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-7" data-testid="careplan-records-summary">
        {[["Total", data.summary?.total], ["Digital plans", data.summary?.digital_present], ["POC sources", data.summary?.poc_source_documents], ["POC task match", `${data.summary?.poc_task_avg_coverage || 0}%`], ["Scans uploaded", data.summary?.scans_uploaded], ["Verified", data.summary?.verified], ["Need correction", data.summary?.needs_correction]].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm"><p className="text-xs text-stone-500">{label}</p><p className="mt-1 font-heading text-2xl font-semibold text-stone-900">{value || 0}</p></div>
        ))}
      </div>

      <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-4">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client…" className="h-8 w-56 border-stone-300" data-testid="careplan-records-search" />
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 rounded-md border border-stone-300 bg-white px-2 text-xs" data-testid="careplan-records-filter">
            <option value="all">All records</option><option value="scan">Signed scan pending</option><option value="review">Pending review</option><option value="correction">Needs correction</option><option value="missing">Digital content missing</option>
          </select>
          <span className="text-xs text-stone-400" data-testid="careplan-records-count">Showing {rows.length} of {data.rows?.length || 0}</span>
        </div>
        <div className="hidden grid-cols-[1.2fr_0.7fr_0.7fr_1fr_0.9fr_0.8fr] gap-2 border-b border-stone-100 px-4 py-2 text-[10px] font-bold uppercase tracking-wide text-stone-400 lg:grid">
          <span>Client</span><span>Effective</span><span>Digital plan</span><span>Wet signature</span><span>Match</span><span>Verified by</span>
        </div>
        <div className="max-h-[42rem] overflow-y-auto" data-testid="careplan-records-list">
          {rows.map((row) => <RecordRow key={row.id} row={row} onChanged={load} />)}
          {rows.length === 0 && <p className="px-5 py-10 text-center text-sm text-stone-400" data-testid="careplan-records-empty">No records match this filter.</p>}
        </div>
      </div>
    </div>
  );
}
